import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import IconButton from "@mui/material/IconButton";
import UploadIcon from "../../assets/images/Svg/UploadIcon";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ThreeSixtyIcon from "@mui/icons-material/ThreeSixty";
import { colors } from "../../theme/colors";
import { fonts } from "../../theme/fonts";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "../../context/Internationalization";
import { fileIcons, getMimeType } from "../../utility/Utils";
import {
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";

const { Primary, Grey, Error } = colors;
const { text } = fonts;

const useStyles = makeStyles((_theme) => ({
  label: {
    ...text.sm.medium,
    color: Grey[700],
  },
  highlightText: {
    ...text.sm.normal,
    color: Primary[700],
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  errorText: {
    ...text.sm.normal,
    color: Error[500],
  },
  text: {
    ...text.sm.normal,
    color: Grey[500],
  },
  previewText: {
    ...text.sm.normal,
    color: Grey[500],
    textAlign: "center",
    display: "block",
  },
  uploadFileContainer: {
    border: `1px dashed ${Grey[300]}`,
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  cover: {
    width: "100%",
    height: "178px !important",
    objectFit: "cover",
    borderRadius: "8px",
    marginBottom: "8px",
  },
  deleteButton: {
    position: "absolute",
    top: 0,
    right: 0,
    backgroundColor: Error[500],
    "&:hover": {
      backgroundColor: Error[700],
    },
  },
  titleWrap: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
}));

const MultipleFilesUpload = ({
  label,
  name,
  value,
  onChange,
  acceptedFileTypes,
  singleFileUpload,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [fileErrors, setFileErrors] = useState([]);

  // Normalize accept keys (react-dropzone typically expects object like { 'image/*': [] })
  const acceptKeys = acceptedFileTypes ? Object.keys(acceptedFileTypes) : [];

  const areImagesAllowed = acceptKeys.some((type) => type.startsWith("image/"));
  const areVideosAllowed = acceptKeys.some((type) => type.startsWith("video/"));
  const areDocumentsAllowed = acceptKeys.some((type) =>
    type.startsWith("application/")
  );

  const matchesAccept = (fileType) => {
    if (!fileType) return false;
    if (acceptKeys.length === 0) return true; // if nothing specified, accept all
    return acceptKeys.some((k) => {
      if (k.endsWith("/*")) {
        const prefix = k.slice(0, k.indexOf("/"));
        return fileType.startsWith(`${prefix}/`);
      }
      return k === fileType;
    });
  };

  const getFileIcon = (fileType) => {
    let icon = null;
    fileIcons.forEach((fileIcon) => {
      if (fileIcon.type === fileType) icon = fileIcon.icon;
    });
    return icon;
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const newErrors = [];
      const newFiles = singleFileUpload
        ? []
        : Array.isArray(value)
        ? [...value]
        : [];

      acceptedFiles.forEach((selectedFile) => {
        // Validate type
        if (!matchesAccept(selectedFile.type)) {
          newErrors.push(t("invalid_file_type"));
        }

        // Validate size (<= 15MB)
        if (
          (areImagesAllowed || areVideosAllowed || areDocumentsAllowed) &&
          selectedFile.size > 15728640
        ) {
          newErrors.push(t("invalid_file_size"));
        }

        if (newErrors.length === 0) {
          if (singleFileUpload) {
            newFiles[0] = selectedFile;
          } else {
            newFiles.push(selectedFile);
          }
        }
      });

      setFileErrors(newErrors);

      if (newErrors.length === 0) {
        onChange({ target: { name, value: newFiles } });
      }
    },
    [
      onChange,
      name,
      value,
      areImagesAllowed,
      areVideosAllowed,
      areDocumentsAllowed,
      singleFileUpload,
      t,
    ]
  );

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    accept: acceptedFileTypes || undefined,
    noClick: value && value.length > 0,
    onError: (err) => console.log("dropzone error", err),
  });

  const addFile = () => {
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  const removeFile = (index, event) => {
    event.stopPropagation();
    const newFiles = Array.isArray(value) ? [...value] : [];
    newFiles.splice(index, 1);
    onChange({ target: { name, value: newFiles } });
  };

  // Safely infer meta for either File/Blob or string (URL/path)
  const getFileMeta = (file) => {
    if (!file) return { type: "", src: "", name: "" };

    const isBlob =
      (typeof File !== "undefined" && file instanceof File) ||
      (typeof Blob !== "undefined" && file instanceof Blob);

    // src
    const src = isBlob ? URL.createObjectURL(file) : String(file || "");

    // filename (strip query/hash)
    const lastSegment = (isBlob ? file.name : src.split("/").pop() || "") || "";
    const cleanName = String(lastSegment).split("?")[0].split("#")[0];
    const name = cleanName || (isBlob ? "file" : "file");

    // mime type
    const fromObj = isBlob ? file.type || "" : "";
    const ext = (name.includes(".") ? name.split(".").pop() : "").toLowerCase();
    const inferred = ext ? getMimeType(ext) || "" : "";
    const dataUrlType = src.startsWith("data:image")
      ? "image/*"
      : src.startsWith("data:video")
      ? "video/*"
      : "";

    const type = fromObj || inferred || dataUrlType || "";

    return { type, src, name };
  };

  const renderPreview = (file, index) => {
    const { type, src, name: fileName } = getFileMeta(file);
    const safeType = String(type || "");
    const isImageType = safeType.startsWith("image/");
    const isVideoType = safeType.startsWith("video/");

    return (
      <ImageListItem key={index}>
        {/* Preview */}
        {isImageType && src && (
          <img
            className={styles.cover}
            src={src}
            loading="lazy"
            alt={fileName || "image"}
          />
        )}
        {isVideoType && src && (
          <video autoPlay loop muted className={styles.cover}>
            <source src={src} />
          </video>
        )}
        {!isImageType && !isVideoType && getFileIcon(safeType)}

        <ImageListItemBar
          title={<div className={styles.titleWrap}>{fileName}</div>}
          actionIcon={
            <>
              {/* Delete */}
              <IconButton
                aria-label={t("delete")}
                className={styles.deleteButton}
                color={"neutral"}
                onClick={(event) => removeFile(index, event)}
              >
                <DeleteIcon />
              </IconButton>
            </>
          }
        />
      </ImageListItem>
    );
  };

  const renderFileErrors = (errs) => {
    if (!errs || errs.length === 0) return null;
    return (
      <span className={styles.errorText}>
        {errs.map((error, index) => (
          <React.Fragment key={index}>
            {index > 0 && <br />}
            {error}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const renderButton = () => {
    return (
      <Button
        {...getRootProps()}
        onClick={addFile}
        fullWidth
        startIcon={singleFileUpload ? <ThreeSixtyIcon /> : <AddIcon />}
        sx={{ mt: 2 }}
      >
        {singleFileUpload ? t("replace_file") : t("add_file")}
      </Button>
    );
  };

  const renderUploadArea = () => {
    return (
      <>
        <input {...getInputProps()} />
        <UploadIcon />
        {isDragActive ? (
          <p>
            <span className={styles.text}>{t("drop_files_here")}</span>
          </p>
        ) : (
          <>
            <div>
              <span className={styles.highlightText}>
                {t("click_to_upload")}
              </span>{" "}
              <span className={styles.text}>{t("drag_n_drop")}</span>
            </div>
            {fileErrors.length > 0 ? (
              renderFileErrors(fileErrors)
            ) : (
              <span className={styles.text}>
                {areImagesAllowed && <strong>{t("image_upload_files")}</strong>}
                {areVideosAllowed && (
                  <>
                    <br />
                    <strong>{t("video_upload_files")}</strong>
                  </>
                )}
                {areDocumentsAllowed && (
                  <>
                    <br />
                    <strong>{t("document_upload_files")}</strong>
                  </>
                )}
              </span>
            )}
          </>
        )}
      </>
    );
  };

  const hasFiles =
    Array.isArray(value) &&
    value.filter((item) => !!item && item !== "" && item !== null).length > 0;

  const cleanFiles = hasFiles ? value.filter(Boolean) : [];

  return (
    <div>
      {label && label?.length > 0 && (
        <span className={styles.label}>{label}</span>
      )}
      <div {...getRootProps()} className={styles.uploadFileContainer}>
        {hasFiles ? (
          <>
            {cleanFiles.length === 1 ? (
              renderPreview(cleanFiles[0], 0)
            ) : (
              <ImageList
                sx={{ width: 360, height: 360 }}
                cols={2}
                rowHeight={178}
              >
                {cleanFiles.map((file, index) => renderPreview(file, index))}
              </ImageList>
            )}

            <input {...getInputProps()} style={{ display: "none" }} />
            {renderFileErrors(fileErrors)}
            {renderButton()}
          </>
        ) : (
          renderUploadArea()
        )}
      </div>
    </div>
  );
};

MultipleFilesUpload.propTypes = {};

export default MultipleFilesUpload;
